import './TeaserInfoModal.scss';
import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, FormGroup, ModalBody, ModalHeader, Label } from 'reactstrap';
import Rodal from '../../../components/Rodal/Rodal';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { Form, Input } from '@availity/form';
import createNotification from '../../../utils/createNotification';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import * as yup from 'yup';
import { TeaserPracticeData } from '../../../slices/marketplace/timeline/models/teaser-data.model';
import { LocationTypes } from '../../../slices/marketplace/timeline/models/enums/location-type.enum';
import { OwnershipTypes } from '../../../slices/marketplace/timeline/models/enums/ownership-type.enum';
import { RevenueGrowthRates } from '../../../slices/marketplace/timeline/models/enums/revenue-growth.enum';
import { useStateSelector } from '../../../store/selectors';
import {
    PracticeFocus,
    PracticeFocusOptionsMapping,
} from '../../../utils/constants/PracticeFocus.enum';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import { getAllStates } from '../../../utils/constants';

interface TeaserInfoModalProps {
    timelineId: number;
    teaserData: TeaserPracticeData[];
    isAdmin: boolean;
    isReadOnly: boolean;
    isVisible: boolean;
    onClose: () => void;
    onSaveToTempState?: (newValues: TeaserPracticeData[]) => Promise<void>;
    onUpdateConfirm?: () => void;
    fetchData?: () => void;
}

const TeaserInfoModal = (props: TeaserInfoModalProps) => {
    let modalForm = useRef<FormikProps<{ teaser: TeaserPracticeData[] }>>(null);

    const axios = useStateSelector((state) => state.core.axios);

    const [isSaving, setIsSaving] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);

    const [isConfirmationPopupVisible, setIsConfirmationPopupVisible] =
        useState(false);

    const [allStates, setAllStates] = useState<string[]>();

    const [activeTab, setActiveTab] = useState<number>(
        props.teaserData && props.teaserData.length > 0
            ? props.teaserData[0]?.practiceId
            : 0
    );

    const [practicesData, setPracticesData] = useState<TeaserPracticeData[]>([
        ...props.teaserData.map((practice) =>
            !practice.locations || practice.locations.length == 0
                ? {
                      ...practice,
                      locations: [
                          {
                              id: 0,
                              practiceId: practice.practiceId,
                              fteDvms: undefined,
                              locationType: undefined,
                              ownershipType: undefined,
                              practiceType: undefined,
                              otherPracticeType: undefined,
                              revenueGrowthRate: undefined,
                              state: undefined,
                          },
                      ],
                  }
                : practice
        ),
    ]);

    useEffect(() => {
        axios
            .get('/api/Location/StatesByRegion')
            .then((response) => {
                if (response && response.data && response.data.data) {
                    const allStates = getAllStates(response.data.data, true);
                    setAllStates([...allStates]);
                }
            })
            .catch((error) => {
                createNotification(
                    'An error occured while fetching States',
                    'error'
                );
            });
    }, []);

    const updatePracticeData = (
        practiceId: number,
        locationId: number,
        field: string,
        value: string
    ) => {
        setPracticesData((prev) =>
            prev.map((practice) =>
                practice.practiceId === practiceId
                    ? {
                          ...practice,
                          locations: practice.locations.map((loc) =>
                              loc.id === locationId
                                  ? { ...loc, [field]: value }
                                  : loc
                          ),
                      }
                    : practice
            )
        );
    };

    const renderTabContent = () => {
        if (practicesData.length == 0) return <>No Data</>;

        const selectedPractice = practicesData.find(
            (p) => p.practiceId == activeTab
        );
        const practiceIndex = practicesData.indexOf(selectedPractice);

        return (
            <div
                className="practice-tab-content"
                key={selectedPractice.practiceId}>
                {selectedPractice?.locations &&
                selectedPractice?.locations?.length > 0 &&
                allStates ? (
                    selectedPractice?.locations?.map(
                        (location, locationIndex) => {
                            return (
                                <>
                                    {
                                        // Commented until multiple locations is implemented
                                        // Styles are already added
                                        //<div className="location-title">{`Location ${
                                        //   index + 1
                                        //} : Location DBA`}</div>
                                    }

                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="name">
                                                    State / Province:
                                                </Label>
                                                <Input
                                                    type="select"
                                                    id={`teaser[${practiceIndex}].locations[${locationIndex}].state`}
                                                    name={`teaser[${practiceIndex}].locations[${locationIndex}].state`}
                                                    disabled={props.isReadOnly}
                                                    onChange={(event) => {
                                                        updatePracticeData(
                                                            selectedPractice.practiceId,
                                                            location.id,
                                                            'state',
                                                            allStates[
                                                                Number(
                                                                    event.target
                                                                        .value
                                                                )
                                                            ]
                                                        );
                                                        modalForm.current.setFieldValue(
                                                            `teaser[${practiceIndex}].locations[${locationIndex}].state`,
                                                            event.target.value,
                                                            false
                                                        );
                                                    }}
                                                    onBlur={(e) => {}}
                                                    value={allStates.indexOf(
                                                        location.state
                                                    )}>
                                                    <option
                                                        disabled={true}
                                                        value={-1}
                                                        key={'state0'}>
                                                        Select State
                                                    </option>
                                                    {allStates.map(
                                                        (item, index) => (
                                                            <option
                                                                value={index}
                                                                key={
                                                                    'state' +
                                                                    index
                                                                }>
                                                                {item}
                                                            </option>
                                                        )
                                                    )}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="name">
                                                    Practice Focus:
                                                </Label>
                                                <Input
                                                    type="select"
                                                    id={`teaser[${practiceIndex}].locations[${locationIndex}].practiceType`}
                                                    name={`teaser[${practiceIndex}].locations[${locationIndex}].practiceType`}
                                                    disabled={props.isReadOnly}
                                                    onChange={(event) => {
                                                        const prevOther =
                                                            PracticeFocus.Other ===
                                                            Number(
                                                                practicesData[
                                                                    practiceIndex
                                                                ].locations[
                                                                    locationIndex
                                                                ].practiceType
                                                            );
                                                        const newNotOther =
                                                            PracticeFocus.Other !==
                                                            Number(
                                                                event.target
                                                                    .value
                                                            );
                                                        if (
                                                            prevOther &&
                                                            newNotOther
                                                        ) {
                                                            updatePracticeData(
                                                                selectedPractice.practiceId,
                                                                location.id,
                                                                'otherPracticeType',
                                                                undefined
                                                            );
                                                            modalForm.current.setFieldValue(
                                                                `teaser[${practiceIndex}].locations[${locationIndex}].otherPracticeType`,
                                                                undefined, false
                                                            );
                                                        }
                                                        updatePracticeData(
                                                            selectedPractice.practiceId,
                                                            location.id,
                                                            'practiceType',
                                                            event.target.value
                                                        );
                                                        modalForm.current.setFieldValue(
                                                            `teaser[${practiceIndex}].locations[${locationIndex}].practiceType`,
                                                            event.target.value, false
                                                        );
                                                    }}
                                                    onBlur={(e) => {}}
                                                    defaultValue={0}>
                                                    <option
                                                        disabled={true}
                                                        value={0}
                                                        key={'practiceType0'}>
                                                        Select Practice Focus
                                                    </option>
                                                    {PracticeFocusOptionsMapping.map(
                                                        (item) => (
                                                            <option
                                                                value={item.id}
                                                                key={
                                                                    'practiceType' +
                                                                    item.id
                                                                }>
                                                                {item.label}
                                                            </option>
                                                        )
                                                    )}
                                                </Input>
                                            </FormGroup>
                                            {location.practiceType &&
                                            location.practiceType ==
                                                PracticeFocus.Other ? (
                                                <FormGroup>
                                                    <Label for="name">
                                                        Please Specify:
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        id={`teaser[${practiceIndex}].locations[${locationIndex}].otherPracticeType`}
                                                        name={`teaser[${practiceIndex}].locations[${locationIndex}].otherPracticeType`}
                                                        disabled={
                                                            props.isReadOnly
                                                        }
                                                        className="text-field"
                                                        onChange={(e) => {
                                                            updatePracticeData(
                                                                selectedPractice.practiceId,
                                                                location.id,
                                                                'otherPracticeType',
                                                                e.target.value
                                                            );
                                                            modalForm.current.setFieldValue(
                                                                `teaser[${practiceIndex}].locations[${locationIndex}].otherPracticeType`,
                                                                e.target.value, false
                                                            );
                                                        }}
                                                        onBlur={(e) => {}}
                                                        //defaultValue={''}
                                                    />
                                                </FormGroup>
                                            ) : (
                                                []
                                            )}
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="name">
                                                    Number of FTE DVMs:
                                                </Label>
                                                <Input
                                                    type="number"
                                                    id={`teaser[${practiceIndex}].locations[${locationIndex}].fteDvms`}
                                                    name={`teaser[${practiceIndex}].locations[${locationIndex}].fteDvms`}
                                                    disabled={props.isReadOnly}
                                                    className="text-field"
                                                    onChange={(e) => {
                                                        updatePracticeData(
                                                            selectedPractice.practiceId,
                                                            location.id,
                                                            'fteDvms',
                                                            e.target.value
                                                        );
                                                        modalForm.current.setFieldValue(
                                                            `teaser[${practiceIndex}].locations[${locationIndex}].fteDvms`,
                                                            e.target.value,
                                                            false
                                                        );
                                                    }}
                                                    onBlur={(e) => {}}
                                                    placeholder="Enter Number of FTE DVMs"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="name">
                                                    Location Type:
                                                </Label>
                                                <Input
                                                    type="select"
                                                    id={`teaser[${practiceIndex}].locations[${locationIndex}].locationType`}
                                                    name={`teaser[${practiceIndex}].locations[${locationIndex}].locationType`}
                                                    disabled={props.isReadOnly}
                                                    onChange={(event) => {
                                                        updatePracticeData(
                                                            selectedPractice.practiceId,
                                                            location.id,
                                                            'locationType',
                                                            event.target.value
                                                        );
                                                        modalForm.current.setFieldValue(
                                                            `teaser[${practiceIndex}].locations[${locationIndex}].locationType`,
                                                            event.target.value, false
                                                        );
                                                    }}
                                                    onBlur={(e) => {}}
                                                    defaultValue={0}>
                                                    <option
                                                        disabled={true}
                                                        value={0}
                                                        key={'locationType0'}>
                                                        Select Location Type
                                                    </option>
                                                    {LocationTypes.map(
                                                        (item) => (
                                                            <option
                                                                value={item.id}
                                                                key={
                                                                    'locationType' +
                                                                    item.id
                                                                }>
                                                                {item.name}
                                                            </option>
                                                        )
                                                    )}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="name">
                                                    Revenue Growth Rate:
                                                </Label>
                                                <Input
                                                    type="select"
                                                    id={`teaser[${practiceIndex}].locations[${locationIndex}].revenueGrowthRate`}
                                                    name={`teaser[${practiceIndex}].locations[${locationIndex}].revenueGrowthRate`}
                                                    disabled={props.isReadOnly}
                                                    onChange={(event) => {
                                                        updatePracticeData(
                                                            selectedPractice.practiceId,
                                                            location.id,
                                                            'revenueGrowthRate',
                                                            event.target.value
                                                        );
                                                        modalForm.current.setFieldValue(
                                                            `teaser[${practiceIndex}].locations[${locationIndex}].revenueGrowthRate`,
                                                            event.target.value, false
                                                        );
                                                    }}
                                                    onBlur={(e) => {}}
                                                    defaultValue={0}>
                                                    <option
                                                        disabled={true}
                                                        value={0}
                                                        key={
                                                            'revenueGrowthRate0'
                                                        }>
                                                        Select Range of Growth
                                                        Rate
                                                    </option>
                                                    {RevenueGrowthRates.map(
                                                        (item) => (
                                                            <option
                                                                value={item.id}
                                                                key={
                                                                    'revenueGrowthRate' +
                                                                    item.id
                                                                }>
                                                                {item.name}
                                                            </option>
                                                        )
                                                    )}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="name">
                                                    Ownership:
                                                </Label>
                                                <Input
                                                    type="select"
                                                    id={`teaser[${practiceIndex}].locations[${locationIndex}].ownershipType`}
                                                    name={`teaser[${practiceIndex}].locations[${locationIndex}].ownershipType`}
                                                    disabled={props.isReadOnly}
                                                    onChange={(event) => {
                                                        updatePracticeData(
                                                            selectedPractice.practiceId,
                                                            location.id,
                                                            'ownershipType',
                                                            event.target.value
                                                        );
                                                        modalForm.current.setFieldValue(
                                                            `teaser[${practiceIndex}].locations[${locationIndex}].ownershipType`,
                                                            event.target.value, false
                                                        );
                                                    }}
                                                    onBlur={(e) => {}}
                                                    defaultValue={0}>
                                                    <option
                                                        disabled={true}
                                                        value={0}
                                                        key={'ownershipType0'}>
                                                        Select Ownership
                                                    </option>
                                                    {OwnershipTypes.map(
                                                        (item) => (
                                                            <option
                                                                value={item.id}
                                                                key={
                                                                    'ownershipType' +
                                                                    item.id
                                                                }>
                                                                {item.name}
                                                            </option>
                                                        )
                                                    )}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </>
                            );
                        }
                    )
                ) : (
                    <>No Data</>
                )}
            </div>
        );
    };

    const hasTabError = (
        touched: FormikTouched<TeaserPracticeData[]>,
        errors: FormikErrors<TeaserPracticeData[]>,
        index: number
    ) => {
        if (!errors || !touched) return false;

        if (Object.hasOwn(touched, index) && Object.hasOwn(errors, index)) {
            const locationsTouched = touched[index]?.locations;
            const locationsErrors = errors[index]?.locations;

            if (!locationsTouched || !locationsErrors) return false;

            // Refine for multiple locations
            const firstLocationTouched = locationsTouched.at(0) as any;
            const firstLocationError = locationsErrors.at(0) as any;
            // --------------------------------

            for (const key of Object.keys(firstLocationTouched)) {
                if (firstLocationTouched[key] && firstLocationError[key]) {
                    return true;
                }
            }
        }

        return false;
    };

    const renderPracticeTabs = (
        errors: FormikErrors<TeaserPracticeData[]>,
        touched: FormikTouched<TeaserPracticeData[]>
    ) => {
        return (
            <div>
                <div className="RRT__container mt-10">
                    <div className="RRT__tabs body-tabs">
                        {practicesData.map(
                            (teaserPracticeData, practiceIndex) => {
                                let className =
                                    activeTab === teaserPracticeData.practiceId
                                        ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                        : 'RRT__tab';

                                className += hasTabError(
                                    touched,
                                    errors,
                                    practiceIndex
                                )
                                    ? ' mui-error'
                                    : '';

                                return (
                                    <div
                                        key={practiceIndex}
                                        className={className}
                                        onClick={() =>
                                            setActiveTab(
                                                teaserPracticeData.practiceId
                                            )
                                        }>
                                        {teaserPracticeData.practiceName}
                                    </div>
                                );
                            }
                        )}
                    </div>
                </div>
                {renderTabContent()}
            </div>
        );
    };

    const initSubmit = (confirm: boolean) => {
        const validationPromise = confirm
            ? modalForm.current.validateForm()
            : Promise.resolve(
                  {} as FormikErrors<{ teaser: TeaserPracticeData[] }>
              );
        validationPromise.then(
            (errors: FormikErrors<{ teaser: TeaserPracticeData[] }>) => {
                if (modalForm.current.isValid || !confirm) {
                    if (!confirm && props.isAdmin && props.onSaveToTempState) {
                        props
                            .onSaveToTempState([...practicesData])
                            .then(() => props.onClose())
                            .catch((error) => {});
                    } else {
                        if (confirm) {
                            setIsConfirming(true);
                            axios
                                .put(
                                    `/api/Marketplace/Engagements/${props.timelineId}/ConfirmTeaser`,
                                    { practiceData: practicesData }
                                )
                                .then((response: any) => {
                                    if (response && response.status === 200) {
                                        if (props.onUpdateConfirm)
                                            props.onUpdateConfirm();
                                        if (props.fetchData) props.fetchData();
                                        createNotification(
                                            'Teaser successfully confirmed.',
                                            'success'
                                        );
                                    } else {
                                        createNotification(
                                            'An error occurred while confirming Teaser.',
                                            'error'
                                        );
                                    }
                                })
                                .catch((error) => {
                                    createNotification(
                                        'An error occurred while confirming Teaser.',
                                        'error'
                                    );
                                })
                                .finally(() => {
                                    setIsConfirming(false);
                                    props.onClose();
                                });
                        } else {
                            setIsSaving(true);
                            axios
                                .put(
                                    `/api/Marketplace/Engagements/${props.timelineId}/teaser`,
                                    { practiceData: practicesData }
                                )
                                .then((response: any) => {
                                    if (response && response.status === 200) {
                                        if (props.fetchData) props.fetchData();
                                        createNotification(
                                            'Teaser Info successfully saved.',
                                            'success'
                                        );
                                    } else {
                                        createNotification(
                                            'An error occurred while saving Teaser Info.',
                                            'error'
                                        );
                                    }
                                })
                                .catch((error) => {
                                    createNotification(
                                        'An error occurred while saving Teaser Info.',
                                        'error'
                                    );
                                })
                                .finally(() => {
                                    setIsSaving(false);
                                    props.onClose();
                                });
                        }
                    }
                } else modalForm.current.handleSubmit();
            }
        );
    };

    return (
        <div className="teaser-info-modal">
            <Rodal
                visible={props.isVisible}
                animation={'fade'}
                onClose={props.onClose}
                showMask={true}
                width={600}
                closeOnEsc
                className="teaser-rodal"
                center>
                <ModalHeader className="popup-header">
                    <div className="d-flex align-items-center">
                        <span>Teaser Information</span>
                    </div>
                </ModalHeader>
                <ModalBody>
                    <div className="teaser-header-text">
                        Please review this teaser containing high-level
                        information about your practices for accuracy. Potental
                        buyers will use this information to access their
                        interest in your practices.
                    </div>

                    <Form
                        innerRef={modalForm}
                        initialValues={{ teaser: practicesData }}
                        validationSchema={yup.object().shape({
                            teaser: yup.array().of(
                                yup.object().shape({
                                    locations: yup.array().of(
                                        yup.object().shape({
                                            state: yup.string().required(),
                                            practiceType: yup
                                                .number()
                                                .min(1)
                                                .required(),
                                            fteDvms: yup
                                                .number()
                                                .min(0.01)
                                                .max(9999.99)
                                                .required(),
                                            locationType: yup
                                                .number()
                                                .min(1)
                                                .required(),
                                            revenueGrowthRate: yup
                                                .number()
                                                .min(1)
                                                .required(),
                                            ownershipType: yup
                                                .number()
                                                .min(1)
                                                .required(),
                                            otherPracticeType: yup
                                                .string()
                                                .when('practiceType', {
                                                    is: 4,
                                                    then: (schema) =>
                                                        schema.required(
                                                            'Please specify other Practice Focus.'
                                                        ),
                                                    otherwise: (schema) =>
                                                        schema.notRequired(),
                                                }),
                                        })
                                    ),
                                })
                            ),
                        })}
                        validateOnChange={false}
                        validateOnBlur={false}
                        onSubmit={(values, formikHelpers) => {}}>
                        {({ values, errors, touched }) => (
                            <div className="d-flex flex-column">
                                <FormGroup>
                                    {renderPracticeTabs(
                                        errors?.teaser as FormikErrors<
                                            TeaserPracticeData[]
                                        >,
                                        touched?.teaser
                                    )}
                                </FormGroup>

                                <div className="mt-auto d-flex justify-content-end buttons-wrapper">
                                    <button
                                        type="button"
                                        className="btn btn-cancel"
                                        onClick={() => props.onClose()}>
                                        Cancel
                                    </button>
                                    <ButtonLoader
                                        buttonText={`Save and Close`}
                                        loaderButtonText={''}
                                        disabled={props.isReadOnly}
                                        isLoading={isSaving}
                                        onClick={() => initSubmit(false)}
                                        type="button"
                                        className={`btn ${
                                            props.isAdmin
                                                ? 'btn-primary'
                                                : 'btn-cancel'
                                        } loader-submit`}
                                    />
                                    {!props.isAdmin ? (
                                        <ButtonLoader
                                            buttonText={`Confirm Teaser`}
                                            loaderButtonText={''}
                                            disabled={
                                                props.isReadOnly ||
                                                props.isAdmin
                                            }
                                            isLoading={isConfirming}
                                            onClick={() => {
                                                modalForm.current
                                                    .validateForm()
                                                    .then(() => {
                                                        if (
                                                            modalForm.current
                                                                .isValid
                                                        )
                                                            setIsConfirmationPopupVisible(
                                                                true
                                                            );
                                                        else
                                                            modalForm.current.handleSubmit();
                                                    });
                                            }}
                                            type="submit"
                                            className={
                                                'btn btn-primary loader-submit'
                                            }
                                        />
                                    ) : (
                                        []
                                    )}
                                </div>
                            </div>
                        )}
                    </Form>
                </ModalBody>
            </Rodal>
            {isConfirmationPopupVisible && (
                <ConfirmationModal
                    header="Confirm Teaser"
                    isVisible={isConfirmationPopupVisible}
                    setIsVisible={setIsConfirmationPopupVisible}
                    confirmationText="By clicking Confirm, your teaser will be posted for buyers to express interest. Please ensure all data is accurate before confirming."
                    params={''}
                    onConfirm={() => initSubmit(true)}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    isLoading={isConfirming}
                />
            )}
        </div>
    );
};

export default TeaserInfoModal;
